import { creatorbase, creatorbaseQueries, ExpectedResponseError } from '@round/api';
import { useMutation } from '@tanstack/react-query';

const deleteOptions = creatorbaseQueries.posts.options.delete;

type Params = {
    postId?: string | undefined;
};

export function useManageCreatorPosts({ postId }: Params) {
    const createMutation = useMutation({
        // We aren't using the create mutation options from the API package because we need to conditionally set the post status to backup after creation.
        // The API doesn't support this on creation yet, so we need to do it manually.
        mutationFn: async ({ data, status }: { data: creatorbase.PostPostData; status: 'planned' | 'backup' }) => {
            const response = await creatorbase.postPost(data);
            if (response.status !== 201) {
                throw new ExpectedResponseError('Could not create post', response);
            }

            if (status === 'planned') {
                return response.data;
            }

            const statusResponse = await creatorbase.postManualStatus({
                influencer_post_id: response.data.id,
                status: 'backup',
            });

            if (statusResponse.status !== 201) {
                throw new ExpectedResponseError('Could not set post status', statusResponse);
            }

            return { ...response.data, status: statusResponse.data.status } as typeof response.data;
        },
        mutationKey: creatorbaseQueries.posts.keys.create(),
    });

    const deleteMutation = useMutation({
        ...deleteOptions(),
        mutationKey: creatorbaseQueries.posts.keys.delete(postId),
    });

    return {
        create: {
            mutate: createMutation.mutate,
            context: createMutation.variables,
            isLoading: createMutation.isPending,
            data: createMutation.data,
            reset: createMutation.reset,
        },
        delete: {
            mutate: deleteMutation.mutate,
            isLoading: deleteMutation.isPending,
            data: deleteMutation.data,
            reset: deleteMutation.reset,
        },
    };
}
